import { Config } from '../config';
import { EventsFilter, Fleet } from '../interfaces';
import {
  DailyEvents,
  EventsByBus,
  EventsByParkedTime,
  EventsByType,
  EventsPerBus,
  EventsPerDay,
  EventsPerHour,
  EventsPerParkedTime,
  EventsPerType,
  HourlyEvents,
  RepeatObservations,
  RepeatOffences,
} from '../interfaces/insight';
import { EventsService } from './events.service';
import { HttpService } from './http.service';

const path = `${Config.API}/api/v2/insight`;
const spotlightPath = `${Config.API}/api/v2/spotlight`;

export abstract class InsightService {
  public static getObservationsByDay = (fleet: Fleet, filter: EventsFilter): Promise<DailyEvents[]> => {
    const filters = EventsService.sanitizeFilters(filter, fleet.timezone, true);

    return HttpService.fetchAuthorized(`${path}/${fleet.uuid}/events/day`, {
      method: 'POST',
      body: JSON.stringify(filters),
    }).then((res: EventsPerDay) => res.event_data || []);
  };

  public static getObservationsByHour = (fleet: Fleet, filter: EventsFilter): Promise<HourlyEvents[]> => {
    const filters = EventsService.sanitizeFilters(filter, fleet.timezone, true);

    return HttpService.fetchAuthorized(`${path}/${fleet.uuid}/events/hour`, {
      method: 'POST',
      body: JSON.stringify(filters),
    }).then((res: EventsPerHour) => res.event_data || []);
  };

  public static getObservationsByBus = (fleet: Fleet, filter: EventsFilter): Promise<EventsByBus[]> => {
    const filters = EventsService.sanitizeFilters(filter, fleet.timezone, true);

    return HttpService.fetchAuthorized(`${path}/${fleet.uuid}/events/bus`, {
      method: 'POST',
      body: JSON.stringify(filters),
    }).then((res: EventsPerBus) => res.event_data || []);
  };

  public static getRepeatOffencesSnapshot = (fleet: Fleet, filter: EventsFilter): Promise<RepeatOffences[]> => {
    const filters = EventsService.sanitizeFilters(filter, fleet.timezone, true);

    return HttpService.fetchAuthorized(`${path}/${fleet.uuid}/events/repeat/count`, {
      method: 'POST',
      body: JSON.stringify(filters),
    }).then((res: RepeatObservations) => res.event_data || []);
  };

  public static getObservationsByType = (fleet: Fleet, filter: EventsFilter): Promise<EventsByType[]> => {
    const filters = EventsService.sanitizeFilters(filter, fleet.timezone, true);

    return HttpService.fetchAuthorized(`${path}/${fleet.uuid}/events/type`, {
      method: 'POST',
      body: JSON.stringify(filters),
    }).then((res: EventsPerType) => res.event_data || []);
  };

  public static getObservationParkedTime = (fleet: Fleet, filter: EventsFilter): Promise<EventsByParkedTime[]> => {
    const filters = EventsService.sanitizeFilters(filter, fleet.timezone, true);

    return HttpService.fetchAuthorized(`${path}/${fleet.uuid}/events/parked/time`, {
      method: 'POST',
      body: JSON.stringify(filters),
    }).then((res: EventsPerParkedTime) => res.event_data || []);
  };

  public static getEvidencesDeliveredByMonth = async (
    filter: SpotlightFilter,
  ): Promise<{ event_type_id: number; data: { date: string; count: number }[] }[]> => {
    const url = new URL(`${spotlightPath}/evidences_delivered_by_month`, window.location.origin);

    if (filter.startDate) {
      url.searchParams.set('start_date', filter.startDate);
    }
    if (filter.endDate) {
      url.searchParams.set('end_date', filter.endDate);
    }

    return HttpService.fetchAuthorized(url.toString(), {
      method: 'POST',
    });
  };

  public static getCitationsPaidByMonth = async (
    filter: SpotlightFilter,
  ): Promise<{ event_type_id: number; data: { date: string; count: number }[] }[]> => {
    const url = new URL(`${spotlightPath}/citations_paid_by_month`, window.location.origin);

    if (filter.startDate) {
      url.searchParams.set('start_date', filter.startDate);
    }
    if (filter.endDate) {
      url.searchParams.set('end_date', filter.endDate);
    }

    return HttpService.fetchAuthorized(url.toString(), {
      method: 'POST',
    });
  };

  public static getTopLocations = async (
    filter: TopLocations,
  ): Promise<{ event_type_id: number; data: { block_name: string; count: number; route_direction: string }[] }[]> => {
    const url = new URL(`${spotlightPath}/violation_locations_by_month`, window.location.origin);

    if (filter.startDate) {
      url.searchParams.set('start_date', filter.startDate);
    }
    if (filter.endDate) {
      url.searchParams.set('end_date', filter.endDate);
    }

    if (filter.eventTypeIds) {
      url.searchParams.set('event_type_ids', filter.eventTypeIds.join(','));
    }

    return HttpService.fetchAuthorized(url.toString(), {
      method: 'POST',
    });
  };
}

interface SpotlightFilter {
  startDate?: string;
  endDate?: string;
}

interface TopLocations {
  startDate?: string;
  endDate?: string;
  eventTypeIds: number[];
}
